import React, { useState, useEffect } from 'react';
import { Grid, AlertVariant } from 'react-unity';
import TextInputField from '../common/form-controls/TextInputField';
import ToggleField from '../common/form-controls/ToggleField';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import { LinkTreeItem } from '../common/LinkTree';
import { useNavigate, useParams } from 'react-router-dom';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import VirtualMachineRequest from '../../models/entities/VirtualMachineRequest';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import { IStateForm } from '../common/AbstractForm';
import User from '../../models/User';
import ProgressBarBig from './components/ProgressBarBig';
import WorkflowInstance from '../../models/WorkflowInstance';
import VirtualMachine from '../../models/entities/VirtualMachine';
import VirtualMachinesTable from './components/VirtualMachinesTable';
import VirtualMachineRequestService from '../../services/VirtualMachineRequestService';
import WorkflowDetailWrapper from '../common/wrappers/WorkflowDetailWrapper';

export interface IVMRStateForm extends IStateForm {
	id: number;
	environment?: string;
	requester?: User | null;
	subscriptionName: string,
	resourceGroup: string,
	virtualNetworkId: string,
	subnetName: string,
	serverNamePrefix: string,
	publisher: string,
	offer: string,
	SKU: string,
	version: string,
	location: string,
	vmSize: string,
	diskType: string,
	diskSize: number,
	availabilityZone?: string,
	recoveryServices?: string,
	backupPolicy?: string,
	azureBackup: string,
	patchMode: string,
	patchSchedule?: string,
	workflowInstance: WorkflowInstance,
	virtualMachines: VirtualMachine[],
}

const VirtualMachineRequestDetail = () => {

	const { subsId } = useParams<{ subsId: string }>();
	const subscriptionId = parseInt(subsId);
	const { vmrId } = useParams<{ vmrId: string }>();
	const virtualMachineRequestId = parseInt(vmrId);
	const [subscription, setSubscription] = useState<SubscriptionRequest>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [endModal, setEndModal] = useState<AlertBanner>({ visible: false });
	const subscriptionService = new SubscriptionRequestsService();
	const virtualMachineRequestService = new VirtualMachineRequestService();
	const navigate = useNavigate();
	const [stateForm, setStateForm] = useState<IVMRStateForm>(
		{
			id: null,
			subscriptionName: '',
			resourceGroup: '',
			requester: null,
			environment: '',
			virtualNetworkId: '',
			subnetName: '',
			serverNamePrefix: '',
			publisher: '',
			offer: '',
			SKU: '',
			version: '',
			location: '',
			vmSize: '',
			diskType: '',
			diskSize: 0,
			availabilityZone: '',
			recoveryServices: '',
			backupPolicy: '',
			azureBackup: '',
			patchMode: '',
			patchSchedule: '',
			workflowInstance: null,
			virtualMachines: null,
		}
	);

	const retreiveVirtualMachineRequest = async () => {
		try {
			setLoading(true);
			const sub = await subscriptionService.getById(subscriptionId);
			setSubscription(sub);
			const vmr = new VirtualMachineRequest(
				await virtualMachineRequestService.getVirtualMachineRequestById(virtualMachineRequestId)
			);
			setStateForm(stateFormFromRequest(vmr, sub));
		} catch (err) {
			console.error(err);
			handleEndModal(`The virtual machines request could not be retrieved`, 'error', 10000);
		} finally {
			setLoading(false);
		}
	};

	const handleEndModal = (text: string, variant: AlertVariant, timeout: number) => {
		setEndModal({
			visible: true,
			text,
			variant,
		});
		setTimeout(() => endAndRedirect(), timeout);
	};

	const endAndRedirect = () => {
		setEndModal({ visible: false });
		navigate('/');
	};

	const getLinkTree = (): LinkTreeItem[] => {
		return [
			{ text: 'Subscriptions', to: '/subscriptions' },
			{ text: subscription?.createdSubscriptionName, to: `/subscriptions/${subscription?.id}` },
			{ text: 'Virtual Machine Requests', to: `/subscriptions/${subscription?.id}/virtualMachineRequestHistory` }
		];
	};

	const stateFormFromRequest = (vmr: VirtualMachineRequest, sub: SubscriptionRequest): IVMRStateForm => {
		return {
			id: vmr.id,
			subscriptionName: sub.createdSubscriptionName,
			resourceGroup: vmr.resourceGroupName,
			requester: vmr.requesterUser,
			virtualNetworkId: '',
			subnetName: vmr.subnetName,
			serverNamePrefix: '',
			publisher: vmr.imageReference.publisher,
			offer: vmr.imageReference.offer,
			SKU: vmr.imageReference.sku,
			version: vmr.imageReference.version,
			location: vmr.location,
			vmSize: vmr.virtualMachineSize,
			diskType: vmr.osDiskType,
			diskSize: vmr.osDiskSize,
			availabilityZone: '',
			recoveryServices: '',
			backupPolicy: '',
			azureBackup: vmr.azureBackup,
			patchMode: vmr.patchMode,
			patchSchedule: vmr.patchSchedule,
			workflowInstance: vmr.workflowInstance,
			virtualMachines: vmr.virtualMachines,
		};
	};

	const stateFormHandler = () => getStateFormHandlers();

	const getStateFormHandlers = () => {
		return {
			subscriptionName: {
				value: stateForm?.subscriptionName,
				disabled: true,
			},
			resourceGroups: {
				value: stateForm?.resourceGroup,
				disabled: true,
			},
			requester: {
				value: stateForm?.requester?.displayName,
				disabled: true,
			},
			environment: {
				value: stateForm?.environment,
				disabled: true,
			},
			subnetName: {
				value: stateForm?.subnetName,
				disabled: true,
			},
			serverNamePrefix: {
				value: stateForm?.serverNamePrefix,
				disabled: true,
			},
			location: {
				value: stateForm?.location,
				disabled: true,
			},
			publisher: {
				value: stateForm?.publisher,
				disabled: true,
			},
			offer: {
				value: stateForm?.offer,
				disabled: true,
			},
			SKU: {
				value: stateForm?.SKU,
				disabled: true,
			},
			vmSize: {
				value: stateForm?.vmSize,
				disabled: true,
			},
			diskType: {
				value: stateForm?.diskType,
				disabled: true,
			},
			diskSize: {
				value: stateForm?.diskSize,
				disabled: true,
			},
			availabilityZone: {
				value: stateForm?.availabilityZone,
				disabled: true,
				options: [
					{ value: '1', text: 'Zone 1' },
					//{value: '2', text: 'Zone 2'},
					{ value: '3', text: 'Zone 3' }
				],
			},
			recoveryServices: {
				value: stateForm?.recoveryServices,
				disabled: true,
			},
			backupPolicy: {
				value: stateForm?.backupPolicy,
				disabled: true,
			},
			azureBackup: {
				value: stateForm?.azureBackup,
				disabled: true,
				options: [
					{ value: 'Enable', text: 'Enabled' },
					{ value: 'Disable', text: 'Disabled' },
				],
			},
			patchMode: {
				value: stateForm?.patchMode,
				disabled: true,
			},
			patchSchedule: {
				value: stateForm?.patchSchedule,
				disabled: true,
			},
		};
	};

	useEffect(() => {
		retreiveVirtualMachineRequest();
	}, []);

	return (
		<>
			<WorkflowDetailWrapper
				title={`Virtual Machine Request #${stateForm.id}`}
				workflowInstance={stateForm?.workflowInstance}
				loading={loading}
				linkTree={getLinkTree()}
			>
				<Grid variant="halves">
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'Subscription'}
							{...stateFormHandler().subscriptionName} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'Resource Group'}
							{...stateFormHandler().resourceGroups} />
					</Grid.Item>
				</Grid>
				<Grid variant="2-up">
					<Grid.Item>
						<TextInputField
							label={'Requestor'}
							{...stateFormHandler().requester} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'Subnet'}
							{...stateFormHandler().subnetName} />
					</Grid.Item>
				</Grid>
				{stateForm?.workflowInstance?.currentWorkflowInstanceState.workflowState &&
					<Grid variant="halves">
						<Grid.Item>
							<ProgressBarBig
								workflowState={stateForm?.workflowInstance?.currentWorkflowInstanceState.workflowState} />
						</Grid.Item>
					</Grid>}
				<Grid variant="2-up">
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Location'}
							{...stateFormHandler().location} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Size'}
							{...stateFormHandler().vmSize} />
					</Grid.Item>
				</Grid>
				<Grid variant="3-up">
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Image Publisher'}
							{...stateFormHandler().publisher} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Image Offer'}
							{...stateFormHandler().offer} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Image SKUs'}
							{...stateFormHandler().SKU} />
					</Grid.Item>
				</Grid>
				<Grid variant="2-up">
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'Disk Type'}
							{...stateFormHandler().diskType} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'Disk Size'}
							{...stateFormHandler().diskSize} />
					</Grid.Item>
				</Grid>
				{stateForm.availabilityZone !== '0' &&
					<Grid variant="2-up">
						<Grid.Item>
							<TextInputField
								className="full-width"
								label={'Availability Zone'}
								{...stateFormHandler().availabilityZone} />
						</Grid.Item>
						<Grid.Item />
					</Grid>}
				<Grid variant="halves">
					<Grid.Item>
						<ToggleField
							options={[]}
							label={'Azure Backup'}
							{...stateFormHandler().azureBackup} />
					</Grid.Item>
				</Grid>
				{stateFormHandler().azureBackup.value == 'Enable' &&
					<Grid variant="2-up">
						<Grid.Item>
							<TextInputField
								className="full-width"
								label={'Recovery Services'}
								{...stateFormHandler().recoveryServices} />
						</Grid.Item>
						<Grid.Item>
							<TextInputField
								className="full-width"
								label={'Backup Policy'}
								{...stateFormHandler().backupPolicy} />
						</Grid.Item>
					</Grid>}
				<Grid variant="2-up">
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Patching'}
							{...stateFormHandler().patchMode} />
					</Grid.Item>
					<Grid.Item>
						<TextInputField
							className="full-width"
							label={'VM Patching Schedule'}
							{...stateFormHandler().patchSchedule} />
					</Grid.Item>
				</Grid>
				<VirtualMachinesTable virtualMachines={stateForm.virtualMachines} />
			</WorkflowDetailWrapper>
			<AlertModal {...endModal} willTimeout={false} onClose={endAndRedirect} />
		</>
	);
};

export default VirtualMachineRequestDetail;